import type {
  CreateVideoRequest,
  CreateVideoMetadata,
  UploadItem,
  DashboardUploaderS3Cfg,
} from '@/lib/types/Upload';
import type { IngestcfgData } from '@/lib/types/Ingest';
import { FilenameParser } from './FilenameParser';
import { CREDENTIAL_EXPIRATION_BUFFER_SECS } from '@/lib/constants/upload';

/** Aggregate state of the upload queue, for status banners. */
export type UploadQueuePhase = 'empty' | 'sending' | 'all-received' | 'attention';

export interface UploadQueueCounts {
  /** Client still responsible: pending / creating / uploading. */
  inFlight: number;
  processing: number;
  ready: number;
  failed: number;
  cancelled: number;
  total: number;
}

export class UploadUtils {
  /**
   * Build CreateVideoRequest from UploadItem + IngestcfgData.
   * Reference: lib2/model/media/CreateMediaReq.ts -> createVideoReqFromFilename()
   *
   * Published date priority chain (highest wins):
   *   filename-parsed date > ingestcfg.mediaSettings.published > upload time
   *
   * Upload time is the DEFAULT (null here; the server sets published = created,
   * MediaRow.scala:117,137). file.lastModified was the base until 2026-07: a
   * technical mtime shaped by pickers/transfers/editing, it backdated phone
   * uploads to their capture date — batches sorted months down the published-
   * desc grid and customers reported them as lost. Publish date = time of the
   * publishing act; explicit overrides (filename YYYYMMDD token, ingest
   * profile date, post-upload edit) remain for intentional backfills.
   */
  static buildCreateVideoRequest(
    item: UploadItem,
    ingestcfg: IngestcfgData
  ): CreateVideoRequest {
    const mediaSettings = ingestcfg.mediaSettings;
    const parsedMetadata = FilenameParser.parseMetadata(item.filename, item.lastModified ?? undefined);

    // Published priority chain: null (server defaults to now) -> mediaSettings -> parser
    let published: number | null = null;
    published = mediaSettings.published ?? published;
    published = parsedMetadata.published ?? published;

    const metadata: CreateVideoMetadata = {
      adSupported: !!mediaSettings.adSupported,
      availStart: mediaSettings.availStart || null,
      availEnd: mediaSettings.availEnd || null,
      published,
      refId: parsedMetadata.refId,
      tags: item.tags.length > 0 ? item.tags : ingestcfg.tags,
      title: parsedMetadata.title || item.title,
    };

    return {
      mediaStateType: 'active',
      ingestMethodType: 'dashboard',
      ingestcfgId: ingestcfg.ingestcfgId,
      mediaSourceType: 'uploaded_source',
      mediaType: 'video',
      srcFilename: item.filename,
      metadata,
    };
  }

  /**
   * Displayed/reported transfer progress, capped at 99 until the S3
   * CompleteMultipartUpload is confirmed (the upload-success handler is the
   * only place that writes 100). "All bytes sent" is not "received": prod
   * uploads died in the seconds between the last part and the complete call
   * because the bar said 100 and the user left (2026-07-30). The throttled
   * backend rate report uses the same cap, so a DB completed_rate of 100
   * now means the client confirmed the S3 complete — unambiguous forensics.
   */
  static transferProgress(bytesUploaded: number, bytesTotal: number): number {
    if (bytesTotal <= 0) return 0;
    return Math.min(99, Math.round((bytesUploaded / bytesTotal) * 100));
  }

  /**
   * Find the queued item a picked file corresponds to, by Uppy's own
   * duplicate fingerprint (name + size + mtime — identical fingerprint means
   * identical content as far as the browser can know; an edited file gets a
   * new mtime and never matches). Lets a re-added failed file route to
   * part-level resume instead of colliding with Uppy's duplicate rejection
   * and minting a fresh registration (each of which left a phantom media row).
   */
  static findQueuedMatch(
    items: readonly UploadItem[],
    file: { name: string; size: number; lastModified?: number }
  ): UploadItem | null {
    return (
      items.find(
        (i) =>
          i.uppyFileId !== '' &&
          i.filename === file.name &&
          i.fileSize === file.size &&
          (i.lastModified ?? 0) === (file.lastModified || 0)
      ) ?? null
    );
  }

  /**
   * Check if STS credentials are still valid (with 6hr buffer).
   * Returns false if credentials will expire within the buffer period.
   */
  static areCredentialsValid(cfg: DashboardUploaderS3Cfg): boolean {
    const nowSecs = Math.floor(Date.now() / 1000);
    return cfg.expirationTS - nowSecs > CREDENTIAL_EXPIRATION_BUFFER_SECS;
  }

  /**
   * Human-readable file size.
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = bytes / Math.pow(k, i);

    return `${size.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  /** Per-status counts over the upload queue. */
  static getQueueCounts(items: readonly UploadItem[]): UploadQueueCounts {
    const counts: UploadQueueCounts = {
      inFlight: 0,
      processing: 0,
      ready: 0,
      failed: 0,
      cancelled: 0,
      total: items.length,
    };
    for (const item of items) {
      switch (item.status) {
        case 'pending':
        case 'creating':
        case 'uploading':
          counts.inFlight++;
          break;
        case 'processing':
          counts.processing++;
          break;
        case 'ready':
          counts.ready++;
          break;
        case 'error':
          counts.failed++;
          break;
        case 'cancelled':
          counts.cancelled++;
          break;
      }
    }
    return counts;
  }

  /**
   * Queue phase for status banners:
   * - 'sending': client is still responsible for bytes — leaving cancels uploads
   * - 'all-received': EVERY file reached the server, none failed — leaving is safe
   * - 'attention': failures present (other files may have been received) —
   *   failed files never reached the server and must be retried; a green
   *   "safe to sign out" over a partly-failed batch is how lost batches
   *   got reported with "success messages" (2026-07 shopper incident)
   * - 'empty': nothing to report (no items, or cancels only)
   */
  static getQueuePhase(items: readonly UploadItem[]): UploadQueuePhase {
    const counts = this.getQueueCounts(items);
    if (counts.inFlight > 0) return 'sending';
    if (counts.failed > 0) return 'attention';
    if (counts.processing + counts.ready > 0) return 'all-received';
    return 'empty';
  }

  /**
   * Derive MIME type from filename extension.
   * Falls back to 'application/octet-stream' for unknown types.
   */
  static getContentType(filename: string): string {
    const ext = filename.split('.').pop()?.toLowerCase();
    const mimeMap: Record<string, string> = {
      mp4: 'video/mp4',
      mov: 'video/quicktime',
      qt: 'video/quicktime',
      avi: 'video/x-msvideo',
      mkv: 'video/x-matroska',
      webm: 'video/webm',
      m4v: 'video/x-m4v',
      wmv: 'video/x-ms-wmv',
      asf: 'video/x-ms-asf',
      flv: 'video/x-flv',
      mpg: 'video/mpeg',
      mpeg: 'video/mpeg',
      vob: 'video/mpeg',
      mts: 'video/mp2t',
      m2ts: 'video/mp2t',
      m2t: 'video/mp2t',
      ts: 'video/mp2t',
      mxf: 'application/mxf',
      '3gp': 'video/3gpp',
      '3g2': 'video/3gpp2',
    };
    return (ext && mimeMap[ext]) || 'application/octet-stream';
  }
}
